import { Fragment, useMemo, useState } from 'react';
import {
  Autocomplete,
  MenuItem as MuiMenuItem,
  TextFieldProps
} from '@mui/material';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';
import Checkbox from 'components/Checkbox';
import Icon, { ICONS } from 'components/Icon';
import { SelectCheckboxMenuItem } from 'components/Select';
import Text from 'components/Text';
import TextField from 'components/TextField';
import './AdvancedDesignationsSelector.scss';

export type SelectorOptionType = 'individual' | 'group' | 'all' | 'groupName';

export type SelectorOption = {
  label: string;
  value: string;
  type: SelectorOptionType;
  designationsInGroup?: any[];
};

type AdvancedDesignationsSelectorProps = {
  ungroupedDesignations: SelectorOption[];
  designationGroups: SelectorOption[];
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onChange?: (selected: SelectorOption[]) => void;
  defaultValue?: SelectorOption[];
};

const ALL_UUID = uuid();
const GROUP_NAME = uuid();

export const AdvancedDesignationsSelector = ({
  ungroupedDesignations,
  designationGroups,
  error,
  helperText,
  onBlur,
  onChange,
  defaultValue
}: AdvancedDesignationsSelectorProps) => {
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [groupToRender, setGroupToRender] = useState<SelectorOption>(null);

  const currentSelection = defaultValue || [];

  const inputLabelClassName = clsx('ComboCheckBox__label', {
    'ComboCheckBox__label--selected': currentSelection.length
  });

  const allOptions = [...designationGroups, ...ungroupedDesignations];
  const optionsToRender: SelectorOption[] = [
    { label: 'Select All', value: ALL_UUID, type: 'all' },
    ...allOptions
  ];

  const groupMembersToRender: SelectorOption[] = useMemo(() => {
    if (!groupToRender) return [];
    const mappedDesInGroup: SelectorOption[] =
      groupToRender.designationsInGroup.map((d) => ({
        label: d.title,
        value: d.id,
        type: 'group'
      }));

    return [
      { label: groupToRender.label, value: GROUP_NAME, type: 'groupName' },
      ...mappedDesInGroup
    ];
  }, [groupToRender]);

  // count each designation within each selected group as well as each selected ungrouped designation
  const totalNumberOfSelectedDesignations = useMemo(() => {
    let total = 0;
    currentSelection.forEach((d) => {
      if (d.type === 'individual') {
        total += 1;
      } else if (d.type === 'group') {
        total += d.designationsInGroup.length;
      }
    });
    return total;
  }, [currentSelection]);

  const renderGroupMembers = ({
    option,
    props
  }: {
    option: SelectorOption;
    props: React.HtmlHTMLAttributes<HTMLLIElement>;
  }): React.ReactNode => {
    const onGroupReturn = (event: React.MouseEvent | React.KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setGroupToRender(null);
      setShowGroupMembers(false);
    };

    if (option.type === 'groupName') {
      return (
        <MuiMenuItem
          {...{ ...props }}
          onClick={(e) => onGroupReturn(e)}
          onKeyDown={(e) => onGroupReturn(e)}
        >
          <Icon icon={ICONS.CHEVRON_LEFT} />
          <Text variant="h5">{option.label}</Text>
        </MuiMenuItem>
      );
    }
    return (
      // don't spread props to group member - group members are
      // view only and not selectable
      <MuiMenuItem key={option.value}>
        <Text variant="body">{option.label}</Text>
      </MuiMenuItem>
    );
  };

  const renderOptions = ({
    props,
    option,
    selected
  }: {
    props: React.HtmlHTMLAttributes<HTMLLIElement>;
    option: SelectorOption;
    selected: boolean;
  }): React.ReactNode => {
    switch (option.type) {
      case 'all': {
        return (
          <Fragment key={ALL_UUID}>
            <SelectCheckboxMenuItem
              {...props}
              checked={currentSelection.length >= allOptions.length}
              label={option.label}
            />
            <hr className="ComboCheckBox__separator" />
          </Fragment>
        );
      }
      case 'group': {
        const groupAmount = option.designationsInGroup?.length;
        const onGroupExpand = (
          event: React.MouseEvent | React.KeyboardEvent
        ) => {
          event.preventDefault();
          event.stopPropagation();
          if (groupAmount === 0) return;
          setGroupToRender(option);
          setShowGroupMembers(true);
        };
        return (
          <MuiMenuItem
            {...{ ...props }}
            className="MultiselectCheckbox--select adv-des-selector-group-item"
          >
            <Checkbox checked={selected} />
            <div
              className="adv-des-selector-group-item-label"
              onClick={(e) => onGroupExpand(e)}
              onKeyDown={(e) => onGroupExpand(e)}
              role="button"
              tabIndex={0}
            >
              <Text variant="body">
                {option.label}
                <span className="adv-des-selector-group-item-label-amount">
                  {` (${groupAmount})`}
                </span>
              </Text>
              {groupAmount > 0 && <Icon icon={ICONS.CHEVRON_RIGHT} />}
            </div>
          </MuiMenuItem>
        );
      }
      // individual designation is default case
      default: {
        return (
          <div className="adv-des-selector-individual-item" key={option.value}>
            <SelectCheckboxMenuItem
              {...props}
              key={option.value}
              checked={selected}
              label={option.label}
            />
          </div>
        );
      }
    }
  };

  return (
    <Autocomplete
      className="ComboCheckBox adv-des-selector"
      disableClearable
      disableCloseOnSelect
      renderTags={() => null}
      multiple
      options={showGroupMembers ? groupMembersToRender : optionsToRender}
      value={currentSelection}
      onClose={() => {
        setGroupToRender(null);
        setShowGroupMembers(false);
      }}
      onChange={(_event, value, reason) => {
        const values = value as SelectorOption[];
        let newSelection: SelectorOption[];

        if (reason === 'selectOption') {
          // select all is pressed
          if (values.find((v) => v.value === ALL_UUID)) {
            // if only some of the values are selected
            if (currentSelection.length < allOptions.length) {
              // select all
              newSelection = allOptions;
            } else {
              // unselect all
              newSelection = [];
            }
          } else {
            newSelection = values;
          }
        } else if (reason === 'removeOption') {
          newSelection = values.filter((v) => v.value !== ALL_UUID);
        } else {
          // Handle other reasons by defaulting to the provided values
          newSelection = values;
        }

        // Call parent onChange to update external state
        onChange?.(newSelection);
      }}
      getOptionLabel={(option) => option.label}
      renderOption={(props, option, { selected }) =>
        showGroupMembers
          ? renderGroupMembers({ props, option })
          : renderOptions({ props, option, selected })
      }
      popupIcon={<Icon icon={ICONS.CHEVRON_DOWN} />}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={({ ...params }) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          className="ComboCheckBox__text-field"
          InputLabelProps={{
            shrink: false,
            className: inputLabelClassName
          }}
          label={`(${totalNumberOfSelectedDesignations}) Designation${
            totalNumberOfSelectedDesignations === 1 ? '' : 's'
          }`}
        />
      )}
      onBlur={onBlur}
    />
  );
};
